<template>
    <div>
        <v-dialog
            v-model="internalDialog"
            persistent
            :max-width="popup.maxWidth || 900"
            :hide-overlay="useSharedOverlay"
            :content-class="dialogContentClass"
            scrollable
        >
            <v-card class="rounded-0 pa-0">
                <v-card-text class="pa-0">
                    <!-- ✅ 본문 영역 wrapper: overlay를 올리기 위해 relative -->
                    <div
                        class="popup-body"
                        style="min-height: 250px; position: relative;"
                    >
                        <!-- ✅ 본문은 항상 렌더 (이미지 로딩이 시작되게 해야 함) -->
                        <div
                            ref="content"
                            class="editor-content"
                            :style="{
                                opacity: isContentLoading ? 0 : 1,
                                transition: 'opacity .2s ease'
                            }"
                            v-html="normalizedPopupHtml"
                        ></div>

                        <!-- ✅ 로딩 오버레이: 이미지 로딩 중에만 덮어서 보여줌 -->
                        <div
                            v-if="isContentLoading"
                            class="d-flex flex-column align-center justify-center"
                            style="position:absolute; min-height: 250px; max-height: calc(90vh - 40px); inset: 0;"
                        >
                            <v-progress-circular
                                indeterminate
                                color="#e26a0e"
                                size="50"
                                width="5"
                            />

                            <div class="text-center mt-4 text-gray1">
                                <template v-if="$route.params.lang === 'kor'">
                                    <p class="le-heading6">
                                        레코스가 소식을 준비 중입니다.
                                    </p>
                                    <p class="le-text-body2">
                                        잠시만 기다려 주세요.
                                    </p>
                                </template>
                                <template v-else>
                                    <p class="le-heading6">
                                        LecoS is getting things ready.
                                    </p>
                                    <p class="le-text-body2">
                                        Please check back shortly.
                                    </p>
                                </template>
                            </div>
                        </div>
                    </div>
                </v-card-text>

  
                <!-- 하단 버튼 영역 -->
                <v-card-actions class="pa-0">
                    <v-card
                        class="rounded-0 pa-0 w-100"
                        color="#efefef"
                    >
                        <v-card-actions class="pa-0">
                            <button
                                block
                                tile
                                text
                                style="font-weight: normal; flex: 1;"
                                :style="{
                                    'font-size': $vuetify.breakpoint.xsOnly ? '12px' : '14px',
                                    'min-height': $vuetify.breakpoint.xsOnly ? '30px' : '38px'
                                }"
                                :ripple="false"
                                @click="hideForOneDay"
                            >
                                오늘 하루 보지 않기
                            </button>
  
                            <button
                                block
                                tile
                                text
                                style="font-weight: normal; flex: 1; border-left: 1px solid #dcdcdc;"
                                :style="{
                                    'font-size': $vuetify.breakpoint.xsOnly ? '12px' : '14px',
                                    'min-height': $vuetify.breakpoint.xsOnly ? '30px' : '38px'
                                }"
                                @click="closeDialog"
                            >
                                닫기
                            </button>
                        </v-card-actions>
                    </v-card>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>
  
<script>
import Cookies from 'js-cookie';
  
export default {
    name: 'UserMainPopup',
    props: {
        popup: {
            type: Object,
            required: true,
        },
        // 부모에서 overlay 를 직접 관리할지 여부
        useSharedOverlay: {
            type: Boolean,
            default: false,
        },
        // 'left' | 'right' | 'center'
        position: {
            type: String,
            default: 'center',
        },
    },
    data() {
        return {
            internalDialog: true,
            cookieKey: `hideMainPopup_${this.popup.id}`,
            isContentLoading: true, // ✅ 본문 이미지 로딩 상태
        };
    },
    computed: {
        // 좌/우 위치 class
        positionClass() {
            if (!this.$vuetify.breakpoint.lgAndUp || !this.useSharedOverlay) {
                return '';
            }
            if (this.position === 'left') return 'double-popup-left';
            if (this.position === 'right') return 'double-popup-right';
            return '';
        },

        // v-dialog 에 넘길 content-class (문자열)
        dialogContentClass() {
            const base = 'elevation-2 rounded-lg';
            if (this.positionClass) {
                return `${base} ${this.positionClass}`;
            }
            return base;
        },

        /* ============================================================
        * ANCHOR: 팝업 HTML 내 이미지 경로 보정 (User Front)
        *  - DB에는 src="/upload/..." 형태(상대경로)로 저장되어 있음
        *  - User 사이트에서 v-html 렌더링 시, 브라우저는 이를
        *    현재 도메인(예: http://localhost:8888/upload/...) 기준으로 요청해서
        *    이미지가 깨질 수 있다.
        *  - 그래서 "렌더링할 때만" 유저 백엔드(baseURL)를 붙여서 보여준다.
        * ============================================================ */
        userImageBaseUrl() {
            // 예: https://localhost:7243
            return (process.env.VUE_APP_IMAGE_BASE_URL || '').replace(/\/$/, '');
        },

        normalizedPopupHtml() {
            const html = (this.popup && this.popup.contentHtml) ? this.popup.contentHtml : '';
            if (!html) return html;

            // src="/upload/..."  →  src="https://localhost:7243/upload/..."
            // 작은따옴표/큰따옴표 둘 다 대응
            return html
                .replace(/src="\/upload\//g, `src="${this.userImageBaseUrl}/upload/`)
                .replace(/src='\/upload\//g, `src='${this.userImageBaseUrl}/upload/`);
        },
    },
    watch: {
        normalizedPopupHtml() {
            // ✅ HTML이 바뀌면 로딩 다시 시작하고, 이미지 로딩 대기
            this.isContentLoading = true;
            this.$nextTick(() => {
                this.waitForImages();
            });
        },

        internalDialog(val) {
            this.$emit('visible-change', { id: this.popup.id, visible: val });

            // ✅ 팝업이 열릴 때도 다시 체크 (이미지 로딩 재시작)
            if (val) {
                this.isContentLoading = true;
                this.$nextTick(() => {
                    this.waitForImages();
                });
            }
        },
    },
    mounted() {
        const hide = Cookies.get(this.cookieKey);
        this.internalDialog = !hide;
        // 부모에게 현재 visible 상태 알려주기
        this.$emit('visible-change', {
            id: this.popup.id,
            visible: this.internalDialog,
        });

        // ✅ HTML 렌더 이후 이미지 로딩 체크
        this.$nextTick(() => {
            this.waitForImages();
        });
    },
    methods: {
        hideForOneDay() {
            Cookies.set(this.cookieKey, 'true', { expires: 1 });
            this.internalDialog = false;
        },
        closeDialog() {
            this.internalDialog = false;
        },

        /* ============================================================
        * ANCHOR: 팝업 본문 이미지 로딩 감지
        *  - v-html로 렌더된 img 태그들을 직접 조회
        *  - 모두 load / error 되면 로딩 종료
        * ============================================================ */
        waitForImages() {
            const el = this.$refs.content;
            if (!el) {
                this.isContentLoading = false;
                return;
            }

            const images = el.querySelectorAll('img');
            if (images.length === 0) {
                this.isContentLoading = false;
                return;
            }

            let done = 0;
            const total = images.length;

            const finishOne = () => {
                done += 1;
                if (done >= total) this.isContentLoading = false;
            };

            images.forEach(img => {
                if (img.complete) {
                    finishOne();
                } else {
                    img.addEventListener('load', finishOne, { once: true });
                    img.addEventListener('error', finishOne, { once: true });
                }
            });
        },
    },
};
</script>
  